import { useMemo, useState } from "react";
import ChartCard from "./ChartCard";
import SegmentedToggle from "./SegmentedToggle";
import StackedColumns, { EmptyChartNote } from "./charts/StackedColumns";
import { NoteCard } from "./Layout";
import { Freshness } from "./Pills";
import { formatCents, formatNumber } from "../format";
import { viz } from "../styles";
import { VolumeDay } from "../types";
import {
  GRANULARITIES,
  Granularity,
  bucketVolume,
  formatBucketLabel,
  formatBucketTick,
  isPartialBucket,
  windowDays,
} from "../volume";

/**
 * Message volume over time, with a day/week/month toggle.
 *
 * Shared by the Home tab's platform-wide chart and the customer detail page's
 * per-customer one, so a "week" means the same thing on both and neither can
 * drift into its own bucketing rules.
 *
 * **Volume, deliberately not deliverability.** Delivery status lives only in
 * `dailyTwilioLogs`, a Markit-written mirror of Twilio's callbacks; volume comes
 * from Twilio's own Usage Records. That is the distinction behind Jonathan's
 * "the deliverability itself is not important" — this chart answers how much is
 * being sent and by whom, from the source he trusts.
 *
 * One series, not two. Messages and spend move together and differ by three
 * orders of magnitude, so a shared axis would flatten spend onto the baseline
 * and a second axis would invite reading a crossing point as meaningful. Spend
 * rides along in the tooltip and the table instead.
 */
const SERIES = [{ key: "messagesSent", label: "Messages sent", color: viz.series1 }];

/** The table carries spend too — see `ChartTableSpec.columns` for why it can't be a bar. */
const TABLE_COLUMNS = [
  ...SERIES,
  { key: "spendCents", label: "Twilio spend", color: viz.series4 },
];

const VolumeChartCard = ({
  title,
  subtitle,
  days,
  freshness,
  dimmed,
  emptyLabel,
  error,
  loading,
}: {
  title: string;
  subtitle: string;
  days: VolumeDay[];
  freshness: Freshness;
  dimmed?: boolean;
  emptyLabel: string;
  error?: string;
  loading?: boolean;
}) => {
  const [granularity, setGranularity] = useState<Granularity>("day");

  const buckets = useMemo(
    () => bucketVolume(windowDays(days, granularity), granularity),
    [days, granularity]
  );

  const newest = buckets[buckets.length - 1];
  const partial = isPartialBucket(newest, granularity);

  const total = buckets.reduce((sum, bucket) => sum + bucket.messagesSent, 0);
  const spend = buckets.reduce((sum, bucket) => sum + bucket.spendCents, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ChartCard
        title={title}
        subtitle={
          buckets.length > 0
            ? `${subtitle} · ${formatNumber(total)} messages, ${formatCents(spend)} shown`
            : subtitle
        }
        series={SERIES}
        dimmed={dimmed}
        freshness={freshness}
        action={
          <SegmentedToggle
            label="Granularity"
            options={GRANULARITIES}
            value={granularity}
            onChange={setGranularity}
          />
        }
        table={{
          categoryLabel:
            granularity === "day" ? "Day" : granularity === "week" ? "Week of" : "Month",
          columns: TABLE_COLUMNS,
          rows: buckets.map(bucket => ({
            category: formatBucketTick(bucket.date, granularity),
            values: {
              messagesSent: formatNumber(bucket.messagesSent),
              spendCents: formatCents(bucket.spendCents),
            },
          })),
        }}
      >
        {error ? (
          <EmptyChartNote label={error} />
        ) : loading ? (
          <EmptyChartNote label="Loading…" />
        ) : buckets.length === 0 ? (
          <EmptyChartNote label={emptyLabel} />
        ) : (
          <StackedColumns
            data={buckets}
            series={SERIES}
            categoryKey="date"
            tickFormatter={value => formatBucketTick(value, granularity)}
            labelFormatter={value => formatBucketLabel(value, granularity)}
          />
        )}
      </ChartCard>

      {partial && newest && (
        <NoteCard title={`The last ${granularity} is still in progress`}>
          {formatBucketLabel(newest.date, granularity)} covers {newest.dayCount} day
          {newest.dayCount === 1 ? "" : "s"} so far, not a full {granularity}, because
          the nightly rollup stops at yesterday. Its bar is a running total and will
          keep climbing — it is not a drop in volume.
        </NoteCard>
      )}
    </div>
  );
};

export default VolumeChartCard;
